import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import ExcelJS from "exceljs";
import Pagination from "../components/ui/Pagination";
import { bookingsAPI, roomsAPI, vouchersAPI, uploadAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import TimeSelect from "../components/ui/TimeSelect";
import { Booking } from "../types/booking";
import { Room } from "../types/room";
import { User } from "../types/auth";
import { detectShiftType, calculatePriceByShift, calculateDuration, PriceResult } from "../components/calendar/calendarUtils";
import { localDateStr } from "../utils/date";

const PAGE_SIZE = 10;

const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || "http://localhost:3000";

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending:   { label: "Chờ duyệt",   color: "bg-yellow-100 text-yellow-800" },
  confirmed: { label: "Đã xác nhận", color: "bg-green-100 text-green-800" },
  cancelled: { label: "Đã hủy",      color: "bg-red-100 text-red-800" },
  completed: { label: "Hoàn thành",  color: "bg-gray-100 text-gray-700" },
};

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n || 0);
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("vi-VN", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

function shortId(id: string | undefined): string {
  return id ? "#" + id.slice(-6).toUpperCase() : "—";
}

export interface BookingWithExtras extends Booking {
  guestName?: string;
  guestPhone?: string;
  guestEmail?: string;
  status: string;
  finalPrice: number;
  cmndImageUrl?: string;
  editHistory?: EditHistoryEntry[];
  discount?: number;
  calculatedPrice?: number;
  shiftLabel?: string;
  source?: string;
  createdBy?: { _id: string; name: string } | null;
}

// Kênh đặt phòng — dùng cho bộ lọc "Website/LLD/Khóa phòng" (không liên quan ai tạo đơn)
function getBookingSource(booking: BookingWithExtras): "lld" | "website" | "block" {
  const src = booking.source;
  if (src === "block") return "block";
  return (src === "lld" || src === "LLD") ? "lld" : "website";
}

interface EditHistoryEntry {
  editedAt: string;
  editedBy: string;
  reason: string;
  changes?: string;
}

interface EditFormState {
  guestName: string;
  guestPhone: string;
  guestEmail: string;
  finalPrice: number;
  roomId: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
  cmndImageUrl: string;
  guestName2: string;
  cmndImageUrl2: string;
  editReason: string;
}

function bookingRoomId(booking: BookingWithExtras): string {
  return typeof booking.roomId === "object" && booking.roomId ? booking.roomId._id : (booking.roomId as string) || "";
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────
interface EditBookingModalProps {
  booking: BookingWithExtras;
  onClose: () => void;
  onSaved: (updated: BookingWithExtras) => void;
  currentUser: User | null;
}

export function EditBookingModal({ booking, onClose, onSaved, currentUser }: EditBookingModalProps): React.ReactElement {
  const [form, setForm] = useState<EditFormState>({
    guestName:    booking.guestName    || "",
    guestPhone:   booking.guestPhone   || "",
    guestEmail:   booking.guestEmail   || "",
    finalPrice:   booking.finalPrice   ?? 0,
    roomId:       bookingRoomId(booking),
    checkInDate:  booking.checkInDate  || "",
    checkInTime:  booking.checkInTime  || "",
    checkOutDate: booking.checkOutDate || "",
    checkOutTime: booking.checkOutTime || "",
    cmndImageUrl: booking.cmndImageUrl || "",
    guestName2:   booking.guestName2   || "",
    cmndImageUrl2: booking.cmndImageUrl2 || "",
    editReason:   "",
  });
  const [saving, setSaving] = useState<boolean>(false);
  const [timeChanged, setTimeChanged] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploading2, setUploading2] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewUrl2, setPreviewUrl2] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef2 = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const currentRoomId = bookingRoomId(booking);
    roomsAPI.getAll().then(r => setRooms(
      ((r.data.data || []) as Room[])
        .filter(rm => !rm.hidden || rm._id === currentRoomId)
        .sort((a, b) => a.name.localeCompare(b.name, "vi"))
    )).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (field: keyof EditFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    if (["checkInDate", "checkInTime", "checkOutDate", "checkOutTime"].includes(field)) {
      setTimeChanged(true);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview ngay lập tức
    setPreviewUrl(URL.createObjectURL(file));

    // Tên file: CCCD_{mã đơn}.ext — không gắn tên phòng để đổi phòng không bị lệch tên file
    const id6 = booking._id.slice(-6).toUpperCase();
    const fileName = `CCCD_${id6}`;

    setUploading(true);
    setError("");
    try {
      const res = await uploadAPI.cmnd(file, fileName);
      setForm((f) => ({ ...f, cmndImageUrl: res.data.url }));
    } catch (err: any) {
      setError(err?.response?.data?.message || "Upload ảnh thất bại, thử lại");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange2 = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPreviewUrl2(URL.createObjectURL(file));

    const id6 = booking._id.slice(-6).toUpperCase();
    const fileName = `CCCD_${id6}_2`;

    setUploading2(true);
    setError("");
    try {
      const res = await uploadAPI.cmnd(file, fileName);
      setForm((f) => ({ ...f, cmndImageUrl2: res.data.url }));
    } catch (err: any) {
      setError(err?.response?.data?.message || "Upload ảnh thất bại, thử lại");
    } finally {
      setUploading2(false);
    }
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!form.editReason.trim()) {
      setError("Vui lòng nhập lý do chỉnh sửa");
      return;
    }
    setError("");
    setSaving(true);

    const newRoom = rooms.find(r => r._id === form.roomId);
    const oldRoomId = bookingRoomId(booking);

    // Build change summary for history
    const changes: string[] = [];
    if (form.roomId       !== oldRoomId)            changes.push(`Phòng: "${booking.roomName}" → "${newRoom?.name || form.roomId}"`);
    if (form.guestName    !== booking.guestName)    changes.push(`Tên: "${booking.guestName}" → "${form.guestName}"`);
    if (form.guestPhone   !== booking.guestPhone)   changes.push(`SĐT: ${booking.guestPhone} → ${form.guestPhone}`);
    if (form.guestEmail   !== booking.guestEmail)   changes.push(`Email: ${booking.guestEmail} → ${form.guestEmail}`);
    if (+form.finalPrice  !== booking.finalPrice)   changes.push(`Giá: ${formatCurrency(booking.finalPrice)} → ${formatCurrency(+form.finalPrice)}`);
    if (form.checkInDate  !== booking.checkInDate)  changes.push(`Ngày vào: ${booking.checkInDate} → ${form.checkInDate}`);
    if (form.checkInTime  !== booking.checkInTime)  changes.push(`Giờ vào: ${booking.checkInTime} → ${form.checkInTime}`);
    if (form.checkOutDate !== booking.checkOutDate) changes.push(`Ngày ra: ${booking.checkOutDate} → ${form.checkOutDate}`);
    if (form.checkOutTime !== booking.checkOutTime) changes.push(`Giờ ra: ${booking.checkOutTime} → ${form.checkOutTime}`);
    if (form.cmndImageUrl !== booking.cmndImageUrl) changes.push("Đã cập nhật ảnh CCCD");
    if (form.guestName2   !== (booking.guestName2 || "")) changes.push(`Tên người 2: "${booking.guestName2 || ""}" → "${form.guestName2}"`);
    if (form.cmndImageUrl2 !== (booking.cmndImageUrl2 || "")) changes.push("Đã cập nhật ảnh CCCD người 2");

    const historyEntry: EditHistoryEntry = {
      editedAt: new Date().toISOString(),
      editedBy: currentUser?.name || currentUser?.email || "Admin",
      reason:   form.editReason.trim(),
      changes:  changes.join(" | ") || "Không thay đổi nội dung",
    };

    try {
      await bookingsAPI.update(booking._id, {
        roomId:       form.roomId,
        roomName:     newRoom?.name || booking.roomName,
        guestName:    form.guestName.trim(),
        guestPhone:   form.guestPhone.trim(),
        guestEmail:   form.guestEmail.trim(),
        finalPrice:   Number(form.finalPrice),
        checkInDate:  form.checkInDate,
        checkInTime:  form.checkInTime,
        checkOutDate: form.checkOutDate,
        checkOutTime: form.checkOutTime,
        cmndImageUrl: form.cmndImageUrl.trim(),
        guestName2:    form.guestName2.trim(),
        cmndImageUrl2: form.cmndImageUrl2.trim(),
        editReason:   form.editReason.trim(),
        editHistory:  historyEntry,
      });
      onSaved({
        ...booking,
        ...form,
        roomName: newRoom?.name || booking.roomName,
        finalPrice: Number(form.finalPrice),
        editHistory: [...(booking.editHistory || []), historyEntry],
      });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Lưu thất bại, thử lại");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Chỉnh sửa đơn {shortId(booking._id)}</h2>
            <p className="text-sm text-gray-400">{booking.roomName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">

          {/* Phòng */}
          <Section title="Phòng">
            <Field label="Chọn phòng" required>
              <select className={INPUT + " bg-white"} value={form.roomId} onChange={set("roomId")} required>
                {rooms.map(r => (
                  <option key={r._id} value={r._id}>{r.name}{r.hidden ? " (đã ẩn)" : ""}</option>
                ))}
              </select>
            </Field>
          </Section>

          {/* Thông tin khách */}
          <Section title="Thông tin khách hàng">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Họ tên (CCCD)" required>
                <input
                  className={INPUT}
                  value={form.guestName}
                  onChange={set("guestName")}
                  placeholder="Nguyễn Văn A"
                  required
                />
              </Field>
              <Field label="Số điện thoại">
                <input
                  className={INPUT}
                  value={form.guestPhone}
                  onChange={set("guestPhone")}
                  placeholder="0xxxxxxxxx"
                />
              </Field>
              <Field label="Email" cls="sm:col-span-2">
                <input
                  className={INPUT}
                  type="email"
                  value={form.guestEmail}
                  onChange={set("guestEmail")}
                  placeholder="email@example.com"
                />
              </Field>
              <Field label="Họ tên người thứ 2 (CCCD, tuỳ chọn)" cls="sm:col-span-2">
                <input
                  className={INPUT}
                  value={form.guestName2}
                  onChange={set("guestName2")}
                  placeholder="Nguyễn Thị B"
                />
              </Field>
            </div>
          </Section>

          {/* Giờ vào / ra */}
          <Section title="Thời gian">
            {(timeChanged || form.roomId !== bookingRoomId(booking)) && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                ⚠️ Thay đổi giờ/phòng <strong>không tự cập nhật giá</strong>. Vui lòng chỉnh giá thủ công nếu cần.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Ngày vào">
                <input className={INPUT} type="date" value={form.checkInDate} onChange={set("checkInDate")} />
              </Field>
              <Field label="Giờ vào">
                <TimeSelect className={INPUT + " bg-white"} value={form.checkInTime} onChange={(v: string) => { setForm(f => ({ ...f, checkInTime: v })); setTimeChanged(true); }} />
              </Field>
              <Field label="Ngày ra">
                <input className={INPUT} type="date" value={form.checkOutDate} onChange={set("checkOutDate")} />
              </Field>
              <Field label="Giờ ra">
                <TimeSelect className={INPUT + " bg-white"} value={form.checkOutTime} onChange={(v: string) => { setForm(f => ({ ...f, checkOutTime: v })); setTimeChanged(true); }} />
              </Field>
            </div>
          </Section>

          {/* Giá */}
          <Section title="Giá thanh toán">
            <Field label="Giá (VND)">
              <input
                className={INPUT}
                type="number"
                min={0}
                step={1000}
                value={form.finalPrice}
                onChange={set("finalPrice")}
              />
            </Field>
            <p className="text-xs text-gray-400 mt-1">Hiện tại: {formatCurrency(booking.finalPrice)}</p>
          </Section>

          {/* CCCD Drive */}
          <Section title="CCCD">
            {/* File picker */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                {uploading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-coffee-600" />
                    Đang upload...
                  </>
                ) : (
                  <>🪪 Chọn ảnh CCCD</>
                )}
              </button>
              {form.cmndImageUrl && !uploading && (
                <a
                  href={form.cmndImageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline"
                >
                  🔗 Xem trên Drive
                </a>
              )}
            </div>

            {/* Preview ảnh vừa chọn */}
            {previewUrl && (
              <div className="mt-3">
                <img
                  src={previewUrl}
                  alt="Preview CCCD"
                  className="h-32 rounded-lg border border-gray-200 object-cover"
                />
              </div>
            )}

            {/* Nếu đã có ảnh cũ trên Drive mà chưa chọn ảnh mới */}
            {!previewUrl && form.cmndImageUrl && (
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span>Đã có ảnh CCCD</span>
                <a
                  href={form.cmndImageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Xem ảnh hiện tại
                </a>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-2">
              Tên file tự động: <code className="bg-gray-100 px-1 rounded">
                CCCD_{booking._id.slice(-6).toUpperCase()}
              </code>
            </p>

            <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-2">CCCD người thứ 2{form.guestName2.trim() ? ` — ${form.guestName2}` : ""}</p>
                <input
                  ref={fileInputRef2}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange2}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef2.current?.click()}
                    disabled={uploading2}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50 transition-colors"
                  >
                    {uploading2 ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-coffee-600" />
                        Đang upload...
                      </>
                    ) : (
                      <>🪪 Chọn ảnh CCCD (người 2)</>
                    )}
                  </button>
                  {form.cmndImageUrl2 && !uploading2 && (
                    <a
                      href={form.cmndImageUrl2}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      🔗 Xem trên Drive
                    </a>
                  )}
                </div>
                {previewUrl2 && (
                  <div className="mt-3">
                    <img
                      src={previewUrl2}
                      alt="Preview CCCD người 2"
                      className="h-32 rounded-lg border border-gray-200 object-cover"
                    />
                  </div>
                )}
                {!previewUrl2 && form.cmndImageUrl2 && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <span>Đã có ảnh CCCD người 2</span>
                    <a href={form.cmndImageUrl2} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Xem ảnh hiện tại
                    </a>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Tên file tự động: <code className="bg-gray-100 px-1 rounded">
                    CCCD_{booking._id.slice(-6).toUpperCase()}_2
                  </code>
                </p>
            </div>
          </Section>

          {/* Lý do – bắt buộc */}
          <Section title="Lý do chỉnh sửa ✱">
            <textarea
              className={`${INPUT} resize-none`}
              rows={3}
              value={form.editReason}
              onChange={set("editReason")}
              placeholder="Nhập lý do chỉnh sửa đơn này..."
              required
            />
          </Section>

          {/* Lịch sử edit */}
          {booking.editHistory && booking.editHistory.length > 0 && (
            <Section title="Lịch sử chỉnh sửa">
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {[...booking.editHistory].reverse().map((h, i) => (
                  <div key={i} className="text-xs bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="flex justify-between text-gray-500 mb-1">
                      <span className="font-medium text-gray-700">{h.editedBy}</span>
                      <span>{new Date(h.editedAt).toLocaleString("vi-VN", { hour12: false })}</span>
                    </div>
                    <p className="text-gray-600"><span className="font-medium">Lý do:</span> {h.reason}</p>
                    {h.changes && <p className="text-gray-400 mt-0.5">{h.changes}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {error && <p className="text-red-500 text-sm">{error}</p>}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-coffee-600 text-white rounded-lg text-sm font-semibold hover:bg-coffee-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              Lưu thay đổi
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Create Booking Modal ────────────────────────────────────────────────────
interface VoucherInfo {
  _id: string;
  code: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  maxDiscount?: number;
  minPurchase?: number;
  usageLimit: number;
  usedCount: number;
  startDate: string;
  endDate: string;
  status: string;
  applicableRooms: string | string[];
}

interface CreateFormState {
  guestName: string;
  guestPhone: string;
  guestEmail: string;
  roomId: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
  voucherCode: string;
  cmndImageUrl: string;
  guestName2: string;
  cmndImageUrl2: string;
  notes: string;
  source: "23Homestay" | "LLD";
  customPrice: string;
}

function CreateBookingModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (booking: BookingWithExtras) => void;
}): React.ReactElement {
  const today = localDateStr();
  const [form, setForm] = useState<CreateFormState>({
    guestName: "", guestPhone: "", guestEmail: "",
    roomId: "", checkInDate: today, checkInTime: "14:00",
    checkOutDate: today, checkOutTime: "18:00",
    voucherCode: "", cmndImageUrl: "", notes: "",
    guestName2: "", cmndImageUrl2: "",
    source: "23Homestay", customPrice: "",
  });
  const [rooms, setRooms]       = useState<Room[]>([]);
  const [vouchers, setVouchers] = useState<VoucherInfo[]>([]);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploading2, setUploading2] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewUrl2, setPreviewUrl2] = useState<string | null>(null);
  const [voucherApplied, setVoucherApplied] = useState<VoucherInfo | null>(null);
  const [voucherError, setVoucherError] = useState("");
  const [error, setError]       = useState("");
  const fileInputRef            = useRef<HTMLInputElement>(null);
  const fileInputRef2           = useRef<HTMLInputElement>(null);

  useEffect(() => {
    roomsAPI.getAll().then(r => setRooms(
      (r.data.data || [])
        .filter((rm: Room) => !rm.hidden)
        .sort((a: Room, b: Room) => a.name.localeCompare(b.name, "vi"))
    ));
    vouchersAPI.getAll().then(r => setVouchers(r.data.data || []));
  }, []);

  const selectedRoom = useMemo(() => rooms.find(r => r._id === form.roomId) || null, [rooms, form.roomId]);

  const priceResult: PriceResult | null = useMemo(() => {
    if (!selectedRoom || !form.checkInDate || !form.checkInTime || !form.checkOutDate || !form.checkOutTime) return null;
    try {
      const dur = calculateDuration(form.checkInDate, form.checkInTime, form.checkOutDate, form.checkOutTime);
      if (dur <= 0) return null;
      const shift = detectShiftType(form.checkInDate, form.checkInTime, form.checkOutDate, form.checkOutTime);
      return calculatePriceByShift(shift, form.checkInDate, form.checkInTime, form.checkOutDate, form.checkOutTime, selectedRoom);
    } catch { return null; }
  }, [selectedRoom, form.checkInDate, form.checkInTime, form.checkOutDate, form.checkOutTime]);

  const discount = useMemo(() => {
    if (!voucherApplied || !priceResult) return 0;
    const subtotal = priceResult.price;
    let d = 0;
    if (voucherApplied.discountType === "percentage") {
      d = Math.round(subtotal * (voucherApplied.discountValue / 100));
      if (voucherApplied.maxDiscount && d > voucherApplied.maxDiscount) d = voucherApplied.maxDiscount;
    } else {
      d = voucherApplied.discountValue;
    }
    return Math.min(d, subtotal);
  }, [voucherApplied, priceResult]);

  const finalPrice = priceResult ? Math.max(0, priceResult.price - discount) : 0;

  const durationHours = useMemo(() => {
    if (!form.checkInDate || !form.checkInTime || !form.checkOutDate || !form.checkOutTime) return 0;
    try { return Math.ceil(calculateDuration(form.checkInDate, form.checkInTime, form.checkOutDate, form.checkOutTime)); }
    catch { return 0; }
  }, [form.checkInDate, form.checkInTime, form.checkOutDate, form.checkOutTime]);

  const set = (field: keyof CreateFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (field === "voucherCode") { setVoucherApplied(null); setVoucherError(""); }
  };

  const handleApplyVoucher = (): void => {
    const code = form.voucherCode.trim().toUpperCase();
    if (!code) return;
    const now = today;
    const v = vouchers.find(vch => vch.code === code);
    if (!v || v.status !== "active") { setVoucherError("Voucher không hợp lệ hoặc đã ngừng hoạt động"); return; }
    if (now < v.startDate || now > v.endDate) { setVoucherError("Voucher chưa có hiệu lực hoặc đã hết hạn"); return; }
    if (v.usedCount >= v.usageLimit) { setVoucherError("Voucher đã hết lượt sử dụng"); return; }
    if (v.applicableRooms !== "all" && Array.isArray(v.applicableRooms) && !v.applicableRooms.includes(form.roomId)) {
      setVoucherError("Voucher không áp dụng cho phòng này"); return;
    }
    setVoucherApplied(v);
    setVoucherError("");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    const fileName = `CCCD_${Date.now()}`;
    setUploading(true);
    try {
      const res = await uploadAPI.cmnd(file, fileName);
      setForm(f => ({ ...f, cmndImageUrl: res.data.url }));
    } catch (err: any) {
      setError(err?.response?.data?.message || "Upload ảnh thất bại, thử lại");
    } finally { setUploading(false); }
  };

  const handleFileChange2 = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl2(URL.createObjectURL(file));
    const fileName = `CCCD_${Date.now()}_2`;
    setUploading2(true);
    try {
      const res = await uploadAPI.cmnd(file, fileName);
      setForm(f => ({ ...f, cmndImageUrl2: res.data.url }));
    } catch (err: any) {
      setError(err?.response?.data?.message || "Upload ảnh thất bại, thử lại");
    } finally { setUploading2(false); }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!form.roomId) { setError("Vui lòng chọn phòng"); return; }
    if (!form.guestName.trim()) { setError("Vui lòng nhập tên khách"); return; }
    if (!priceResult) { setError("Thời gian không hợp lệ (giờ ra phải sau giờ vào)"); return; }
    setError("");
    setSaving(true);

    const shiftType = detectShiftType(form.checkInDate, form.checkInTime, form.checkOutDate, form.checkOutTime);
    const customPriceNum = form.customPrice !== "" ? Number(form.customPrice) : null;
    const actualFinalPrice = customPriceNum !== null ? customPriceNum : finalPrice;

    try {
      const res = await bookingsAPI.create({
        roomId:         form.roomId,
        roomName:       selectedRoom?.name || "",
        checkInDate:    form.checkInDate,
        checkInTime:    form.checkInTime,
        checkOutDate:   form.checkOutDate,
        checkOutTime:   form.checkOutTime,
        durationHours,
        appliedRateType: priceResult.rateType,
        shiftLabel:     priceResult.shiftLabel,
        calculatedPrice: finalPrice,
        holidaySurcharge: 0,
        discount,
        finalPrice:     actualFinalPrice,
        isNightShift:   shiftType === "overnight",
        status:         "confirmed",
        guestName:      form.guestName.trim(),
        guestPhone:     form.guestPhone.trim(),
        guestEmail:     form.guestEmail.trim(),
        cmndImageUrl:   form.cmndImageUrl,
        guestName2:     form.guestName2.trim(),
        cmndImageUrl2:  form.cmndImageUrl2,
        voucherCode:    voucherApplied ? voucherApplied.code : "",
        notes:          form.notes.trim(),
        source:         form.source,
      });
      toast.success("Tạo đơn thành công!");
      onCreated(res.data.data);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Tạo đơn thất bại, thử lại");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Tạo đơn đặt phòng</h2>
            <p className="text-sm text-gray-400">Dành cho khách walk-in / đặt qua điện thoại</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Nguồn đơn */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600 shrink-0">Nguồn đơn:</span>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-semibold">
              {(["23Homestay", "LLD"] as const).map(src => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, source: src }))}
                  className={`px-5 py-2 transition-colors ${
                    form.source === src
                      ? src === "LLD"
                        ? "bg-amber-500 text-white"
                        : "bg-coffee-600 text-white"
                      : "bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {src}
                </button>
              ))}
            </div>
          </div>

          {/* Phòng */}
          <Section title="Phòng">
            <Field label="Chọn phòng" required>
              <select className={INPUT + " bg-white"} value={form.roomId} onChange={set("roomId")} required>
                <option value="">— Chọn phòng —</option>
                {rooms.map(r => (
                  <option key={r._id} value={r._id}>{r.name}</option>
                ))}
              </select>
            </Field>
          </Section>

          {/* Khách hàng */}
          <Section title="Thông tin khách hàng">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Họ tên (CCCD)" required>
                <input className={INPUT} value={form.guestName} onChange={set("guestName")} placeholder="Nguyễn Văn A" required />
              </Field>
              <Field label="Số điện thoại">
                <input className={INPUT} value={form.guestPhone} onChange={set("guestPhone")} placeholder="0xxxxxxxxx" />
              </Field>
              <Field label="Email" cls="sm:col-span-2">
                <input className={INPUT} type="email" value={form.guestEmail} onChange={set("guestEmail")} placeholder="email@example.com" />
              </Field>
              <Field label="Họ tên người thứ 2 (CCCD, tuỳ chọn)" cls="sm:col-span-2">
                <input className={INPUT} value={form.guestName2} onChange={set("guestName2")} placeholder="Nguyễn Thị B" />
              </Field>
            </div>
          </Section>

          {/* Thời gian */}
          <Section title="Thời gian nhận – trả phòng">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Field label="Ngày vào">
                <input className={INPUT} type="date" value={form.checkInDate} onChange={set("checkInDate")} required />
              </Field>
              <Field label="Giờ vào">
                <TimeSelect className={INPUT + " bg-white"} value={form.checkInTime} onChange={(v: string) => setForm(f => ({ ...f, checkInTime: v }))} />
              </Field>
              <Field label="Ngày ra">
                <input className={INPUT} type="date" value={form.checkOutDate} onChange={set("checkOutDate")} required />
              </Field>
              <Field label="Giờ ra">
                <TimeSelect className={INPUT + " bg-white"} value={form.checkOutTime} onChange={(v: string) => setForm(f => ({ ...f, checkOutTime: v }))} />
              </Field>
            </div>
          </Section>

          {/* Giá tính tự động */}
          <Section title="Giá">
            {priceResult ? (
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Ca</span>
                  <span className="font-medium text-gray-800">{priceResult.shiftLabel}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span className="text-xs text-gray-400">{priceResult.rateType}</span>
                </div>
                <div className="flex justify-between text-gray-600 pt-1 border-t border-gray-200">
                  <span>Giá cơ bản</span>
                  <span className="font-semibold">{formatCurrency(priceResult.price)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Voucher giảm</span>
                    <span className="font-semibold">−{formatCurrency(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-900 font-bold text-base pt-1 border-t border-gray-200">
                  <span>Hệ thống tính</span>
                  <span className="text-coffee-700">{formatCurrency(finalPrice)}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-xl p-4">
                {selectedRoom ? "Chọn giờ vào/ra để tính giá tự động" : "Chọn phòng và giờ vào/ra để tính giá"}
              </p>
            )}

            {/* Giá thực thu override */}
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1 font-medium">Giá thực thu (để trống = dùng giá hệ thống)</label>
                <input
                  type="number"
                  className={INPUT}
                  placeholder={priceResult ? String(finalPrice) : "Chọn phòng và giờ trước"}
                  value={form.customPrice}
                  onChange={set("customPrice")}
                  min={0}
                  step={1000}
                />
              </div>
              {priceResult && form.customPrice !== "" && Number(form.customPrice) !== finalPrice && (
                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-5 whitespace-nowrap">
                  Hệ thống: {formatCurrency(finalPrice)}
                </div>
              )}
            </div>
          </Section>

          {/* Voucher */}
          <Section title="Voucher (tuỳ chọn)">
            <div className="flex gap-2">
              <input
                className={INPUT}
                value={form.voucherCode}
                onChange={set("voucherCode")}
                placeholder="Nhập mã voucher..."
                style={{ textTransform: "uppercase" }}
              />
              <button
                type="button"
                onClick={handleApplyVoucher}
                className="px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold hover:bg-coffee-700 whitespace-nowrap transition-colors"
              >
                Áp dụng
              </button>
            </div>
            {voucherError && <p className="text-red-500 text-xs mt-1">{voucherError}</p>}
            {voucherApplied && (
              <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <span>✓</span>
                <span>
                  <strong>{voucherApplied.code}</strong> —{" "}
                  {voucherApplied.discountType === "percentage"
                    ? `Giảm ${voucherApplied.discountValue}%${voucherApplied.maxDiscount ? ` (tối đa ${formatCurrency(voucherApplied.maxDiscount)})` : ""}`
                    : `Giảm ${formatCurrency(voucherApplied.discountValue)}`}
                </span>
                <button
                  type="button"
                  onClick={() => { setVoucherApplied(null); setForm(f => ({ ...f, voucherCode: "" })); }}
                  className="ml-auto text-gray-400 hover:text-red-500"
                >✕</button>
              </div>
            )}
          </Section>

          {/* CCCD */}
          <Section title="CCCD">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50 transition-colors"
              >
                {uploading
                  ? <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" /> Đang upload...</>
                  : <>🪪 Chọn ảnh CCCD</>
                }
              </button>
              {form.cmndImageUrl && !uploading && (
                <a href={form.cmndImageUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  🔗 Xem ảnh
                </a>
              )}
            </div>
            {previewUrl && (
              <div className="mt-3">
                <img src={previewUrl} alt="Preview CCCD" className="h-32 rounded-lg border border-gray-200 object-cover" />
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-2">CCCD người thứ 2{form.guestName2.trim() ? ` — ${form.guestName2}` : ""}</p>
                <input ref={fileInputRef2} type="file" accept="image/*" className="hidden" onChange={handleFileChange2} />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef2.current?.click()}
                    disabled={uploading2}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50 transition-colors"
                  >
                    {uploading2
                      ? <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" /> Đang upload...</>
                      : <>🪪 Chọn ảnh CCCD (người 2)</>
                    }
                  </button>
                  {form.cmndImageUrl2 && !uploading2 && (
                    <a href={form.cmndImageUrl2} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                      🔗 Xem ảnh
                    </a>
                  )}
                </div>
                {previewUrl2 && (
                  <div className="mt-3">
                    <img src={previewUrl2} alt="Preview CCCD người 2" className="h-32 rounded-lg border border-gray-200 object-cover" />
                  </div>
                )}
            </div>
          </Section>

          {/* Ghi chú */}
          <Section title="Ghi chú (tuỳ chọn)">
            <textarea
              className={`${INPUT} resize-none`}
              rows={2}
              value={form.notes}
              onChange={set("notes")}
              placeholder="Ghi chú thêm về đơn đặt phòng..."
            />
          </Section>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-coffee-600 text-white rounded-lg text-sm font-semibold hover:bg-coffee-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              Tạo đơn
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Lock Room Modal ─────────────────────────────────────────────────────────
function LockRoomModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (booking: BookingWithExtras) => void;
}): React.ReactElement {
  const { user } = useAuth();
  const today = localDateStr();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomId, setRoomId] = useState("");
  const [checkInDate, setCheckInDate] = useState(today);
  const [checkInTime, setCheckInTime] = useState("14:00");
  const [checkOutDate, setCheckOutDate] = useState(today);
  const [checkOutTime, setCheckOutTime] = useState("18:00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    roomsAPI.getAll().then(r => {
      let list: Room[] = (r.data.data || []).filter((rm: Room) => !rm.hidden);
      if (user?.role === "roommaster" && user?.managedRooms && Array.isArray(user.managedRooms)) {
        const managedIds = user.managedRooms.map((rm) => (typeof rm === "object" && rm && "_id" in rm ? (rm as { _id: string })._id : (rm as string)));
        list = list.filter(rm => managedIds.includes(rm._id));
      }
      setRooms(list.sort((a, b) => a.name.localeCompare(b.name, "vi")));
    });
  }, [user]);

  const selectedRoom = rooms.find(r => r._id === roomId) || null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!roomId) { setError("Vui lòng chọn phòng"); return; }
    if (`${checkOutDate}T${checkOutTime}` <= `${checkInDate}T${checkInTime}`) {
      setError("Giờ kết thúc phải sau giờ bắt đầu");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await bookingsAPI.createLock({
        roomId, roomName: selectedRoom?.name || "",
        checkInDate, checkInTime, checkOutDate, checkOutTime,
        notes: notes.trim(),
      });
      toast.success("Đã khóa phòng thành công!");
      onCreated(res.data.data);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Khóa phòng thất bại, thử lại");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">🔒 Khóa phòng</h2>
            <p className="text-sm text-gray-400">Chặn khung giờ này, khách không đặt được — không tính doanh thu</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Chọn phòng *</label>
            <select className={INPUT + " bg-white"} value={roomId} onChange={e => setRoomId(e.target.value)} required>
              <option value="">— Chọn phòng —</option>
              {rooms.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Ngày bắt đầu</label>
              <input className={INPUT} type="date" value={checkInDate} onChange={e => setCheckInDate(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Giờ bắt đầu</label>
              <TimeSelect className={INPUT + " bg-white"} value={checkInTime} onChange={setCheckInTime} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Ngày kết thúc</label>
              <input className={INPUT} type="date" value={checkOutDate} onChange={e => setCheckOutDate(e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Giờ kết thúc</label>
              <TimeSelect className={INPUT + " bg-white"} value={checkOutTime} onChange={setCheckOutTime} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Lý do (tuỳ chọn)</label>
            <input className={INPUT} value={notes} onChange={e => setNotes(e.target.value)} placeholder="VD: Sửa phòng, chụp hình..." />
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              Hủy
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-semibold hover:bg-zinc-900 disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              🔒 Khóa phòng
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const INPUT = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-300 focus:border-coffee-400";

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps): React.ReactElement {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-1 border-b border-gray-100">{title}</h3>
      {children}
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  cls?: string;
}

function Field({ label, children, required, cls = "" }: FieldProps): React.ReactElement {
  return (
    <div className={cls}>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BookingsPage(): React.ReactElement {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<BookingWithExtras[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<"all" | "website" | "lld" | "block">("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [editingBooking, setEditingBooking] = useState<BookingWithExtras | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const canManageAll = ["admin", "superadmin"].includes(user?.role || "");
  const canLock = ["roommaster", "admin", "superadmin"].includes(user?.role || "");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await bookingsAPI.getAll();
      const data = (res.data.data || []).sort(
        (a: BookingWithExtras, b: BookingWithExtras) => new Date(b.createdAt || "").getTime() - new Date(a.createdAt || "").getTime()
      );
      setBookings(data);
    } catch (e) {
      console.error("Failed to load bookings:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id: string, status: string): Promise<void> => {
    setUpdatingId(id);
    try {
      await bookingsAPI.update(id, { status });
      setBookings(prev => prev.map(b => b._id === id ? { ...b, status } : b));
    } catch (e) {
      console.error("Failed to update status:", e);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleResendEmail = async (id: string): Promise<void> => {
    setUpdatingId(id);
    try {
      const res = await bookingsAPI.resendEmail(id);
      toast.success(res.data.message || "Đã gửi email thành công");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Gửi email thất bại");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSaved = (updated: BookingWithExtras): void => {
    setBookings(prev => prev.map(b => b._id === updated._id ? updated : b));
  };

  const handleCreated = (newBooking: BookingWithExtras): void => {
    setBookings(prev => [newBooking, ...prev]);
  };

  const handleDeleteBooking = async (id: string): Promise<void> => {
    try {
      await bookingsAPI.delete(id);
      setBookings(prev => prev.filter(b => b._id !== id));
      toast.success("Đã mở khóa phòng");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Lỗi khi mở khóa");
    }
  };

  const handleExportExcel = async (): Promise<void> => {
    const statusLabel: Record<string, string> = {
      pending: "Chờ duyệt", confirmed: "Đã xác nhận",
      cancelled: "Đã hủy", completed: "Hoàn thành",
    };

    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Đơn đặt phòng");

      sheet.columns = [
        { header: "Mã đơn",     key: "id",          width: 12 },
        { header: "Ngày đặt",   key: "createdAt",    width: 12 },
        { header: "Khách hàng", key: "guestName",    width: 20 },
        { header: "SĐT",        key: "guestPhone",   width: 14 },
        { header: "Email",      key: "guestEmail",   width: 22 },
        { header: "Phòng",      key: "roomName",     width: 12 },
        { header: "Ngày nhận",  key: "checkInDate",  width: 12 },
        { header: "Giờ nhận",   key: "checkInTime",  width: 10 },
        { header: "Ngày trả",   key: "checkOutDate", width: 12 },
        { header: "Giờ trả",    key: "checkOutTime", width: 10 },
        { header: "Ca",         key: "shiftLabel",   width: 14 },
        { header: "Giá (VND)",  key: "finalPrice",   width: 14 },
        { header: "Giảm giá",   key: "discount",     width: 12 },
        { header: "Trạng thái", key: "status",       width: 14 },
        { header: "Ảnh CCCD",   key: "cmnd",         width: 16 },
        { header: "Khách 2",    key: "guestName2",   width: 20 },
        { header: "Ảnh CCCD 2", key: "cmnd2",        width: 16 },
      ];
      sheet.getRow(1).font = { bold: true };

      for (const b of searchFiltered) {
        const row = sheet.addRow({
          id:           shortId(b._id),
          createdAt:    b.createdAt ? new Date(b.createdAt).toLocaleDateString("vi-VN") : "—",
          guestName:    b.guestName || "—",
          guestPhone:   b.guestPhone || "—",
          guestEmail:   b.guestEmail || "—",
          roomName:     b.roomName || "—",
          checkInDate:  b.checkInDate || "—",
          checkInTime:  b.checkInTime || "—",
          checkOutDate: b.checkOutDate || "—",
          checkOutTime: b.checkOutTime || "—",
          shiftLabel:   b.shiftLabel || "—",
          finalPrice:   b.finalPrice || 0,
          discount:     b.discount || 0,
          status:       statusLabel[b.status] || b.status,
          cmnd:         "",
          guestName2:   b.guestName2 || "—",
          cmnd2:        "",
        });
        row.height = 60;

        if (b.cmndImageUrl) {
          try {
            const imgRes = await fetch(b.cmndImageUrl);
            const buffer = await imgRes.arrayBuffer();
            const ext = b.cmndImageUrl.toLowerCase().includes(".png") ? "png" : "jpeg";
            const imageId = workbook.addImage({ buffer, extension: ext });
            sheet.addImage(imageId, {
              tl: { col: 14, row: row.number - 1 },
              ext: { width: 80, height: 60 },
            });
          } catch {
            // Không tải được ảnh (link hỏng/CORS) — bỏ qua, vẫn xuất phần dữ liệu còn lại
          }
        }

        if (b.cmndImageUrl2) {
          try {
            const imgRes = await fetch(b.cmndImageUrl2);
            const buffer = await imgRes.arrayBuffer();
            const ext = b.cmndImageUrl2.toLowerCase().includes(".png") ? "png" : "jpeg";
            const imageId = workbook.addImage({ buffer, extension: ext });
            sheet.addImage(imageId, {
              tl: { col: 16, row: row.number - 1 },
              ext: { width: 80, height: 60 },
            });
          } catch {
            // Không tải được ảnh (link hỏng/CORS) — bỏ qua, vẫn xuất phần dữ liệu còn lại
          }
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const today = localDateStr();
      const tabLabel = tabs.find(t => t.key === statusFilter)?.label || "Tat-ca";
      const sourceLabel = sourceTabs.find(t => t.key === sourceFilter)?.label || "";
      const nameParts = [
        tabLabel,
        sourceFilter !== "all" ? sourceLabel : "",
        monthFilter !== "all" ? monthLabel(monthFilter) : "",
      ].filter(Boolean);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DonDatPhong_${nameParts.join("_")}_${today}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Xuất Excel thất bại, thử lại");
    } finally {
      setExporting(false);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const handleSyncLLD = async (): Promise<void> => {
    setSyncing(true);
    try {
      await bookingsAPI.syncLLD();
      toast.success("Đồng bộ LLD sheet thành công!");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Đồng bộ thất bại");
    } finally {
      setSyncing(false);
    }
  };

  const counts: Record<string, number> = {
    all:       bookings.length,
    pending:   bookings.filter(b => b.status === "pending").length,
    confirmed: bookings.filter(b => b.status === "confirmed").length,
    cancelled: bookings.filter(b => b.status === "cancelled").length,
    completed: bookings.filter(b => b.status === "completed").length,
  };

  const tabs = [
    { key: "all",       label: "Tất cả" },
    { key: "pending",   label: "Chờ duyệt" },
    { key: "confirmed", label: "Đã xác nhận" },
    { key: "cancelled", label: "Đã hủy" },
    { key: "completed", label: "Hoàn thành" },
  ];

  const sourceCounts = {
    all:     bookings.length,
    website: bookings.filter(b => getBookingSource(b) === "website").length,
    lld:     bookings.filter(b => getBookingSource(b) === "lld").length,
    block:   bookings.filter(b => getBookingSource(b) === "block").length,
  };

  const sourceTabs = [
    { key: "all" as const,     label: "Tất cả nguồn" },
    { key: "website" as const, label: "Website" },
    { key: "lld" as const,     label: "LLD" },
    { key: "block" as const,   label: "🔒 Khóa phòng" },
  ];

  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    bookings.forEach(b => { if (b.checkInDate) months.add(b.checkInDate.slice(0, 7)); });
    return Array.from(months).sort((a, b) => b.localeCompare(a));
  }, [bookings]);

  const monthLabel = (ym: string) => {
    const [y, m] = ym.split("-");
    return `Tháng ${parseInt(m, 10)}/${y}`;
  };

  const filtered = (statusFilter === "all"
    ? bookings
    : bookings.filter(b => b.status === statusFilter)
  )
    .filter(b => sourceFilter === "all" || getBookingSource(b) === sourceFilter)
    .filter(b => monthFilter === "all" || (b.checkInDate || "").slice(0, 7) === monthFilter);

  const searchFiltered = useMemo(() => {
    if (!search.trim()) return filtered;
    const q = search.trim().toLowerCase();
    return filtered.filter(b =>
      (b.guestName || "").toLowerCase().includes(q) ||
      (b.guestPhone || "").toLowerCase().includes(q) ||
      (b.roomName || "").toLowerCase().includes(q) ||
      b._id.slice(-6).toLowerCase().includes(q)
    );
  }, [filtered, search]);

  useEffect(() => { setPage(1); }, [statusFilter, sourceFilter, monthFilter, search]);

  const totalPages = Math.ceil(searchFiltered.length / PAGE_SIZE);
  const pageBookings = useMemo(
    () => searchFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [searchFiltered, page]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Đơn đặt phòng</h1>
          <p className="text-gray-500 mt-1">Danh sách tất cả đơn đặt phòng từ khách hàng</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canLock && (
            <button
              onClick={() => setShowLockModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white rounded-lg text-sm font-semibold hover:bg-zinc-900 transition-colors"
            >
              🔒 Khóa phòng
            </button>
          )}
          {canManageAll && (
            <>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold hover:bg-coffee-700 transition-colors"
              >
                ✚ Tạo đơn
              </button>
              <button
                onClick={handleExportExcel}
                disabled={searchFiltered.length === 0 || exporting}
                className="flex items-center gap-2 px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold hover:bg-coffee-700 disabled:opacity-60 transition-colors"
              >
                {exporting ? "⏳ Đang xuất..." : "📥 Xuất Excel"}
              </button>
              <button
                onClick={handleSyncLLD}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition-colors"
              >
                {syncing ? "⏳ Đang đồng bộ..." : "🔄 Đồng bộ LLD Sheet"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === tab.key
                ? "bg-coffee-600 text-white shadow-sm"
                : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            {tab.label}
            <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs font-semibold ${
              statusFilter === tab.key ? "bg-coffee-500 text-white" : "bg-gray-100 text-gray-600"
            }`}>
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Source Tabs */}
      <div className="flex gap-2 flex-wrap">
        {sourceTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setSourceFilter(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sourceFilter === tab.key
                ? "bg-zinc-800 text-white shadow-sm"
                : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
            }`}
          >
            {tab.label}
            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
              sourceFilter === tab.key ? "bg-zinc-600 text-white" : "bg-gray-100 text-gray-600"
            }`}>
              {sourceCounts[tab.key]}
            </span>
          </button>
        ))}
        {availableMonths.length > 0 && (
          <select
            value={monthFilter}
            onChange={e => setMonthFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-gray-500 border border-gray-200 focus:outline-none focus:ring-1 focus:ring-coffee-400"
          >
            <option value="all">Tất cả tháng</option>
            {availableMonths.map(ym => (
              <option key={ym} value={ym}>{monthLabel(ym)}</option>
            ))}
          </select>
        )}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input
          type="text"
          placeholder="Tìm tên khách, SĐT, phòng, mã đơn..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 text-sm focus:outline-none text-gray-700 placeholder-gray-400"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
        )}
        <span className="text-xs text-gray-400 whitespace-nowrap">{searchFiltered.length} / {filtered.length} đơn</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coffee-600" />
          </div>
        ) : searchFiltered.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm">Không có đơn nào</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500">
                  <th className="px-4 py-3 font-medium">Mã đơn</th>
                  <th className="px-4 py-3 font-medium">Ngày đặt</th>
                  <th className="px-4 py-3 font-medium">Khách hàng</th>
                  <th className="px-4 py-3 font-medium">Phòng</th>
                  <th className="px-4 py-3 font-medium">Nhận – Trả phòng</th>
                  <th className="px-4 py-3 font-medium">Ca</th>
                  <th className="px-4 py-3 font-medium text-right">Giá</th>
                  <th className="px-4 py-3 font-medium text-center">Trạng thái</th>
                  <th className="px-4 py-3 font-medium text-center">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pageBookings.map(booking => (
                  <BookingRow
                    key={booking._id}
                    booking={booking}
                    updating={updatingId === booking._id}
                    onUpdateStatus={updateStatus}
                    onEdit={() => setEditingBooking(booking)}
                    onResendEmail={handleResendEmail}
                    onDelete={handleDeleteBooking}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} totalPages={totalPages} total={searchFiltered.length} pageSize={PAGE_SIZE} onChange={setPage} />
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateBookingModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}

      {/* Lock Room Modal */}
      {showLockModal && (
        <LockRoomModal
          onClose={() => setShowLockModal(false)}
          onCreated={handleCreated}
        />
      )}

      {/* Edit Modal */}
      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          currentUser={user}
          onClose={() => setEditingBooking(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

interface BookingRowProps {
  booking: BookingWithExtras;
  updating: boolean;
  onUpdateStatus: (id: string, status: string) => void;
  onEdit: () => void;
  onResendEmail: (id: string) => void;
  onDelete: (id: string) => void;
}

function formatLockTime(dateStr: string, timeStr: string): string {
  if (!dateStr || !timeStr) return "—";
  const hour = parseInt(timeStr.split(":")[0], 10);
  const [, m, d] = dateStr.split("-").map(Number);
  return `${hour}h (${d}/${m})`;
}

function buildLockShareText(booking: BookingWithExtras): string {
  return [
    `Phòng ${booking.roomName}`,
    `Checkin : ${formatLockTime(booking.checkInDate, booking.checkInTime)}`,
    `Checkout : ${formatLockTime(booking.checkOutDate, booking.checkOutTime)}`,
    "@All lock dùm em",
  ].join("\n");
}

function BookingRow({ booking, updating, onUpdateStatus, onEdit, onResendEmail, onDelete }: BookingRowProps): React.ReactElement {
  const cfg = STATUS_CONFIG[booking.status] || { label: "Không rõ", color: "" };
  const hasEdits = (booking.editHistory?.length || 0) > 0;
  const [linkCopied, setLinkCopied] = useState(false);
  const [formCopied, setFormCopied] = useState(false);

  const handleCopyCheckinLink = () => {
    const url = `${PUBLIC_URL}/checkin/${booking._id}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const handleCopyLockForm = () => {
    navigator.clipboard.writeText(buildLockShareText(booking)).then(() => {
      setFormCopied(true);
      setTimeout(() => setFormCopied(false), 2000);
    });
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* Mã đơn */}
      <td className="px-4 py-3">
        <span className="font-mono text-coffee-600 font-semibold text-sm">
          {shortId(booking._id)}
        </span>
        {(() => {
          const sourceKind = getBookingSource(booking);
          const adminName = booking.createdBy?.name;
          return (
            <>
              {sourceKind === "block" ? (
                <span className="block w-fit mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-zinc-200 text-zinc-700">
                  🔒 Khóa phòng
                </span>
              ) : sourceKind === "lld" ? (
                <span className="block w-fit mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">
                  LLD
                </span>
              ) : (
                <span className="block w-fit mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-600">
                  Website
                </span>
              )}
              {booking.createdBy && (
                <span className="block w-fit mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-coffee-100 text-coffee-700">
                  {adminName ? `Admin: ${adminName}` : "Admin"}
                </span>
              )}
            </>
          );
        })()}
        {hasEdits && (
          <span className="block text-[10px] text-orange-500 font-medium mt-0.5">
            ✏️ Đã sửa {booking.editHistory!.length} lần
          </span>
        )}
        {booking.cmndImageUrl && (
          <a
            href={booking.cmndImageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-[10px] text-blue-500 hover:underline mt-0.5"
          >
            🪪 CCCD
          </a>
        )}
        {booking.cmndImageUrl2 && (
          <a
            href={booking.cmndImageUrl2}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-[10px] text-blue-500 hover:underline mt-0.5"
          >
            🪪 CCCD (2)
          </a>
        )}
      </td>

      {/* Ngày đặt */}
      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
        {formatDate(booking.createdAt)}
      </td>

      {/* Khách hàng */}
      <td className="px-4 py-3">
        <p className="font-semibold text-gray-900">{booking.guestName || "—"}</p>
        {booking.guestEmail && <p className="text-gray-400 text-xs mt-0.5">{booking.guestEmail}</p>}
        {booking.guestPhone && <p className="text-gray-400 text-xs">{booking.guestPhone}</p>}
        {booking.guestName2 && <p className="text-gray-400 text-xs mt-0.5">+ {booking.guestName2}</p>}
      </td>

      {/* Phòng */}
      <td className="px-4 py-3 font-medium text-gray-800">{booking.roomName}</td>

      {/* Thời gian */}
      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
        <p>{booking.checkInDate} <span className="font-semibold">{booking.checkInTime}</span></p>
        <p className="text-gray-400">→ {booking.checkOutDate} <span className="font-semibold">{booking.checkOutTime}</span></p>
      </td>

      {/* Ca */}
      <td className="px-4 py-3 text-gray-600 text-xs">{booking.shiftLabel || "—"}</td>

      {/* Giá */}
      <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
        {(booking.finalPrice == null || (booking.finalPrice === 0 && booking.source === "lld"))
          ? <span className="text-gray-300 font-normal text-xs">—</span>
          : <>
              {formatCurrency(booking.finalPrice)}
              {(booking.discount || 0) > 0 && (
                <p className="text-xs text-green-600 font-normal">-{formatCurrency(booking.discount || 0)}</p>
              )}
              {booking.calculatedPrice != null &&
               booking.calculatedPrice !== booking.finalPrice &&
               booking.calculatedPrice > 0 && (
                <p className="text-[10px] text-amber-600 font-normal">
                  HT: {formatCurrency(booking.calculatedPrice)}
                </p>
              )}
            </>
        }
      </td>

      {/* Trạng thái */}
      <td className="px-4 py-3 text-center">
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cfg.color}`}>
          {cfg.label}
        </span>
        {booking.paymentConfirmedVia === "sepay" && (
          <span className="block w-fit mx-auto mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700">
            🤖 Tự động qua SePay
          </span>
        )}
      </td>

      {/* Thao tác */}
      <td className="px-4 py-3 text-center">
        {updating ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-coffee-500" />
          </div>
        ) : booking.source === "block" ? (
          <div className="flex gap-1 justify-center flex-wrap">
            <button
              onClick={() => { if (window.confirm("Mở khóa khung giờ này?")) onDelete(booking._id); }}
              className="px-2.5 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
            >
              🔓 Mở khóa
            </button>
          </div>
        ) : (
          <div className="flex gap-1 justify-center flex-wrap">
            {/* Link check-in cá nhân */}
            <button
              onClick={handleCopyCheckinLink}
              title={`Copy link check-in: /checkin/${booking._id}`}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                linkCopied
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {linkCopied ? "✓ Đã copy" : "🔗 Link"}
            </button>

            {/* Copy form báo lock cho nhóm */}
            <button
              onClick={handleCopyLockForm}
              title="Copy form báo lock phòng cho nhóm"
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                formCopied
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {formCopied ? "✓ Đã copy" : "🔒 Lock"}
            </button>

            {/* Edit button – luôn hiển thị */}
            <button
              onClick={onEdit}
              className="px-2.5 py-1 bg-coffee-100 text-coffee-700 rounded-lg text-xs font-medium hover:bg-coffee-200 transition-colors"
            >
              ✏️ Sửa
            </button>

            {booking.status === "pending" && (
              <>
                <button
                  onClick={() => onUpdateStatus(booking._id, "confirmed")}
                  className="px-2.5 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200 transition-colors"
                >
                  Duyệt
                </button>
                <button
                  onClick={() => onUpdateStatus(booking._id, "cancelled")}
                  className="px-2.5 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
                >
                  Hủy
                </button>
              </>
            )}
            {booking.status === "confirmed" && (
              <>
                <button
                  onClick={() => onUpdateStatus(booking._id, "completed")}
                  className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
                >
                  Hoàn thành
                </button>
                {booking.guestEmail && (
                  <button
                    onClick={() => onResendEmail(booking._id)}
                    className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 transition-colors"
                    title={`Gửi lại email xác nhận tới ${booking.guestEmail}`}
                  >
                    📧 Gửi lại
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
